/**
 * Pure helpers shared by the Publish dialog (chat) and the Published Reports
 * dashboard plugin: visibility / permission rules, deep links, provenance
 * parsing and the title default (publish-report spec, Req 2.2, 5.6, 6.1).
 *
 * These mirror the server-side rules in the publishReport / unpublishReport
 * resolvers (SponsoredProductsSPFInfraCommon). The server stays authoritative:
 * the UI only hides controls the server would reject anyway.
 */

import type {
  PublishedReport,
  PublishedReportSource,
  PublishedReportSummary,
  ReportRefreshMode,
  ReportRunScopeInput,
} from "../../models/API";

/** The FINN team, which alone manages Global resources. */
export const FINN_TEAM_ID = 1;

export const PUBLISHED_REPORTS_DASHBOARD_ID = "published-reports";

export type ReportVisibility = "private" | "public" | "global";

export const TITLE_MAX_LENGTH = 200;
export const TITLE_DEFAULT_LENGTH = 120;

export function isOperatorOrAdmin(effectiveRole: string | undefined): boolean {
  return effectiveRole === "admin" || effectiveRole === "operator";
}

/**
 * Which visibilities the actor may SET for a report in `teamId` (Req 2.2):
 * private for everyone; public for operators/admins; global only for
 * operators/admins publishing on the FINN team.
 */
export function allowedVisibilities(
  effectiveRole: string | undefined,
  teamId: number | undefined
): ReportVisibility[] {
  const allowed: ReportVisibility[] = ["private"];
  if (isOperatorOrAdmin(effectiveRole)) {
    allowed.push("public");
    if (teamId === FINN_TEAM_ID) allowed.push("global");
  }
  return allowed;
}

export function visibilityOf(report: {
  is_public: boolean;
  is_global: boolean;
}): ReportVisibility {
  if (report.is_global) return "global";
  if (report.is_public) return "public";
  return "private";
}

export function visibilityFlags(visibility: ReportVisibility): {
  is_public: boolean;
  is_global: boolean;
} {
  return {
    is_public: visibility === "public" || visibility === "global",
    is_global: visibility === "global",
  };
}

export const VISIBILITY_LABELS: Record<ReportVisibility, string> = {
  private: "Private (only you can see)",
  public: "Public (visible to team)",
  global: "Global (visible to all teams)",
};

/** Knowledge-card colours (KnowledgePage.getCardStyle) for the visibility stripe. */
export const VISIBILITY_STYLE: Record<
  ReportVisibility,
  { backgroundColor: string; borderColor: string; chipLabel: string }
> = {
  private: {
    backgroundColor: "rgba(21, 101, 192, 0.03)",
    borderColor: "#1565c0",
    chipLabel: "Private",
  },
  public: {
    backgroundColor: "rgba(46, 125, 50, 0.03)",
    borderColor: "#2e7d32",
    chipLabel: "Public",
  },
  global: {
    backgroundColor: "rgba(255, 152, 0, 0.03)",
    borderColor: "#ff9800",
    chipLabel: "Global",
  },
};

export interface ManageContext {
  userId: number | undefined;
  /** The actor's effective role in the CURRENT team. */
  effectiveRole: string | undefined;
  currentTeamId: number | undefined;
}

/**
 * Whether the actor may edit / unpublish `report` (Req 6.1, mirrors the SQL
 * manage predicate): its creator; or, for a team-public report of the current
 * team, an operator/admin of that team; or, for a global report, an
 * operator/admin acting on the FINN team.
 *
 * The UI only knows the actor's role in the current team, so a team-public
 * report of ANOTHER team is manageable here only by its creator (the server
 * would allow that team's operators too, but they manage it from that team).
 */
export function canManagePublishedReport(
  report: Pick<
    PublishedReport,
    "creator_id" | "team_id" | "is_public" | "is_global"
  >,
  ctx: ManageContext
): boolean {
  if (ctx.userId !== undefined && report.creator_id === ctx.userId) return true;
  if (!isOperatorOrAdmin(ctx.effectiveRole)) return false;
  if (report.is_global) return ctx.currentTeamId === FINN_TEAM_ID;
  if (report.is_public) return report.team_id === ctx.currentTeamId;
  return false;
}

/** Default title: the question, trimmed to TITLE_DEFAULT_LENGTH characters. */
export function defaultTitleFor(question: string): string {
  const oneLine = (question ?? "").replace(/\s+/g, " ").trim();
  if (oneLine.length <= TITLE_DEFAULT_LENGTH) return oneLine;
  return oneLine.slice(0, TITLE_DEFAULT_LENGTH - 1).trimEnd() + "…";
}

/** Null when valid, otherwise the inline validation message (Req 2.4). */
export function validateTitle(title: string): string | null {
  const trimmed = title.trim();
  if (trimmed.length === 0) return "Title is required";
  if (trimmed.length > TITLE_MAX_LENGTH)
    return `Title must be ${TITLE_MAX_LENGTH} characters or less`;
  return null;
}

/**
 * Deep link to one report (Req 5.6, 5.7). Carries `team` so a reader who is a
 * member of the report's team is switched into it by MainPage's `?team=`
 * handling; readers outside it see the "not available" notice.
 */
export function publishedReportDeepLink(
  report: Pick<PublishedReportSummary, "id" | "team_name">,
  origin: string = window.location.origin
): string {
  const params = new URLSearchParams();
  params.set("view", "dashboard");
  params.set("dashboard", PUBLISHED_REPORTS_DASHBOARD_ID);
  params.set("report", String(report.id));
  if (report.team_name) params.set("team", report.team_name);
  return `${origin}/?${params.toString()}`;
}

/** Parse PublishedReport.source (JSON text, possibly null/malformed) defensively. */
export function parsePublishedReportSource(
  raw: string | null | undefined
): PublishedReportSource {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Decode the hex-encoded `request_scope` stamp of a stored turn (Question.request_scope)
 * into an object, or undefined when absent or malformed (Req 4.2).
 */
export function decodeRequestScope(
  hexToString: (hex: string) => string,
  raw: string | null | undefined
): PublishedReportSource["request_scope"] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(hexToString(raw));
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Distinct tool names from a turn's TOOLCALLS marker payload, in first-use order. */
export function toolNamesOf(toolCalls: any[] | undefined): string[] {
  const names: string[] = [];
  (toolCalls ?? []).forEach((call) => {
    const name =
      typeof call?.name === "string"
        ? call.name
        : typeof call?.tool === "string"
        ? call.tool
        : typeof call?.tool_name === "string"
        ? call.tool_name
        : null;
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
}

/** Build the provenance JSON stored with a new report (design §Provenance JSON). */
export function buildProvenance(args: {
  requestScope?: PublishedReportSource["request_scope"];
  toolCalls?: any[];
  sources?: any[];
  /** F2 rerun reports: the chat question / turn the defining question came from. */
  originalQuestion?: string;
  originalTurnId?: string;
}): PublishedReportSource {
  const provenance: PublishedReportSource = {
    request_scope: args.requestScope ?? null,
    tools_used: toolNamesOf(args.toolCalls),
    sources_count: args.sources?.length ?? 0,
    answered_at: Math.floor(Date.now() / 1000),
  };
  if (args.originalQuestion !== undefined) {
    provenance.original_question = args.originalQuestion;
    provenance.original_turn_id = args.originalTurnId ?? null;
  }
  return provenance;
}

// --- Refreshable reports (F2, refreshable-report spec) ---------------------

/** The global instruction skill every report run is executed with. */
export const REPORT_FORMAT_SKILL = "report-format";

/** Default polling timeout for a report run when no request timeout is configured. */
export const DEFAULT_RUN_TIMEOUT_SECONDS = 300;

/** Rows published before F2 (no refresh_mode) read as snapshots. */
export function refreshModeOf(
  report: Pick<PublishedReport, "refresh_mode"> | null | undefined
): ReportRefreshMode {
  return report?.refresh_mode === "rerun" ? "rerun" : "snapshot";
}

/** The thread prefix of a turn id ({thread}#{epoch}#{uuid}). */
export function threadOf(turnId: string): string {
  return (turnId ?? "").split("#")[0];
}

/**
 * Whether the actor may trigger "Refresh now" (Req 4.1): a rerun report they
 * may manage, with no run already in flight.
 */
export function canRefresh(
  report: Pick<
    PublishedReport,
    | "creator_id"
    | "team_id"
    | "is_public"
    | "is_global"
    | "refresh_mode"
    | "last_run_status"
  >,
  ctx: ManageContext
): boolean {
  return (
    refreshModeOf(report) === "rerun" &&
    report.last_run_status !== "running" &&
    canManagePublishedReport(report, ctx)
  );
}

/**
 * The scope a report run is executed with (Req 2.3): the source turn's
 * request_scope (team, model, data sources, knowledge, skills) with the
 * report-format skill added once. A turn without a stamp runs on `teamId`
 * with every data source (the backend's defaults).
 */
export function buildRunScope(
  requestScope: PublishedReportSource["request_scope"] | undefined,
  teamId: number
): ReportRunScopeInput {
  const scope = requestScope ?? null;
  const skills = (scope?.skills ?? []).filter(
    (s): s is string => typeof s === "string" && s.length > 0
  );
  if (!skills.includes(REPORT_FORMAT_SKILL)) skills.push(REPORT_FORMAT_SKILL);
  return {
    team_id: scope?.team_id ?? teamId,
    model_id: scope?.model_id ?? null,
    data_source_ids: scope?.data_source_ids ?? null,
    knowledge_base_ids: scope?.knowledge_base_ids ?? null,
    skills,
  };
}

/** Upper bound the runner Lambda accepts for a defining question. */
export const DEFINING_QUESTION_MAX_LENGTH = 4000;

/** Null when the defining question can be run, otherwise the inline message. */
export function validateDefiningQuestion(question: string): string | null {
  const trimmed = (question ?? "").trim();
  if (trimmed.length === 0) return "A question is required to run the report";
  if (trimmed.length > DEFINING_QUESTION_MAX_LENGTH)
    return `The question must be ${DEFINING_QUESTION_MAX_LENGTH} characters or less`;
  return null;
}

/** mm:ss for an elapsed-time display. */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest < 10 ? "0" : ""}${rest}`;
}

/** Human label for a run status / error pair shown in run lists and banners. */
export function runStatusLabel(
  status: string | null | undefined,
  errorType?: string | null
): string {
  if (status === "succeeded") return "Succeeded";
  if (status === "running") return "Running";
  if (status === "failed") {
    if (errorType === "ContentTooLarge") return "Failed (answer too large)";
    if (errorType === "Timeout") return "Failed (timed out)";
    return errorType ? `Failed (${errorType})` : "Failed";
  }
  return "Never run";
}

/**
 * A `running` run older than the request timeout (plus a margin for the queue)
 * can no longer finish inside its browser session and should be reconciled
 * (D6): resolved from its stored turn when the viewer can read it, else failed.
 */
export function isRunStale(
  startedAt: string,
  timeoutSeconds: number,
  now: number = Date.now()
): boolean {
  const started = new Date(startedAt.replace(" ", "T") + "Z").getTime();
  if (Number.isNaN(started)) return false;
  return now - started > (timeoutSeconds + 120) * 1000;
}

/** Short human label for a Bedrock model id, e.g. "us.anthropic.claude-opus-4-7..." -> "claude-opus-4-7". */
export function modelLabel(modelId: string | null | undefined): string {
  if (!modelId) return "unknown";
  // e.g. "us.anthropic.claude-opus-4-7-20260101-v1:0": drop the vendor prefix,
  // the optional YYYYMMDD snapshot and the optional "-vN:M" version suffix.
  const match = modelId.match(
    /anthropic\.([a-z0-9-]+?)(?:-\d{8})?(?:-v\d+(?::\d+)?)?$/i
  );
  return match ? match[1] : modelId;
}
